import { useState, type ReactNode } from 'react';
import { RotateCcw } from 'lucide-react';
import { api } from '../../api';
import { Button } from '../ui/button';
import gmailLogo from '../../assets/logos/gmail.svg';
import { IntegrationCard, type CardMessage, type Fact, BlockedAction } from './IntegrationCard';
import { gmailHealth, mailFreshness, relativeTime, sendingLine, withFreshness, type GmailState, type IntegrationsState,
  type MailCheck, type MailboxSendingHealth } from './model';
import type { MailboxAuth } from '../../../../src/application/gmail/mailbox-auth.js';

/**
 * Shown only when something would hurt delivery: passing checks are not news. The record-level detail is for whoever
 * manages the domain's DNS, so it sits under one plain sentence.
 */
function MailboxHealth({ auth }: { auth: MailboxAuth }) {
  const status = { pass: 'Pass', warn: 'Warn', fail: 'Fail' } as const;
  const problems = auth.checks.filter(check => check.status !== 'pass');
  if (!problems.length) return null;
  return <section className="mailbox-health" aria-label="Email deliverability">
    <div className="mailbox-health-head"><h4>Some emails from {auth.domain} may land in spam</h4>
      <span>Checked {relativeTime(auth.checkedAt)}</span></div>
    <p>Send the steps below to whoever manages your domain.</p>
    <ul>{problems.map(check => <li key={check.id} data-check={check.id} data-status={check.status}>
      <span className="mailbox-auth-label">{check.label}</span>
      <span className={`mailbox-auth-status is-${check.status}`}>{status[check.status]}</span>
      <span className="mailbox-auth-detail">{check.detail}</span>
      {check.fix && <span className="mailbox-auth-fix">{check.fix}</span>}
    </li>)}</ul>
  </section>;
}

const sendingStatus = { green: { tone: 'pass', label: 'Healthy' }, amber: { tone: 'warn', label: 'Watch' },
  red: { tone: 'fail', label: 'Stopped or failing' } } as const;
const percent = (rate: number) => `${(rate * 100).toFixed(1)}%`;

/**
 * Red or green for each sending mailbox: how old it is, today's volume against the day's cap (a new mailbox ramps up),
 * first-contact email against its own cap, and the recent bounce rate. Owners only.
 */
export function SendingHealth({ mailboxes }: { mailboxes: MailboxSendingHealth[] }) {
  if (!mailboxes.length) return null;
  return <section className="mailbox-health" aria-label="Mailbox sending health" data-testid="sending-health">
    {mailboxes.map(box => {
      const status = sendingStatus[box.status];
      return <div key={box.email} data-mailbox={box.email} data-status={box.status}>
        <div className="mailbox-health-head"><h4>{box.email}</h4>
          <span className={`mailbox-auth-status is-${status.tone}`}>{status.label}</span></div>
        <ul>
          <li><span className="mailbox-auth-label">Age</span>
            <span className="mailbox-auth-detail">{box.ageDays === 0 ? 'Connected today' : `${box.ageDays} day${box.ageDays === 1 ? '' : 's'}`}</span></li>
          <li><span className="mailbox-auth-label">Sent today</span>
            <span className="mailbox-auth-detail">{box.sentToday} of {box.dailyCap}</span></li>
          <li><span className="mailbox-auth-label">First contact today</span>
            <span className="mailbox-auth-detail">{box.firstTouchToday} of {box.firstTouchCap}</span></li>
          <li><span className="mailbox-auth-label">Bounces, {box.bounceWindowDays} days</span>
            <span className="mailbox-auth-detail">{box.bouncedRecent} of {box.sentRecent} ({percent(box.bounceRate)}); complaints are not reported by Gmail</span></li>
        </ul>
        {box.reasons.length > 0 && <p>{box.reasons.join('. ')}.</p>}
      </div>;
    })}
  </section>;
}

/**
 * The mailbox Alder sends approved emails from and reads customer replies in. Google returns here with ?gmail=.
 *
 * Slots: `actions` joins the card's action row and `children` adds status rows under the facts, so mailbox features
 * (for example "Send test email" and its result line) drop in without touching the shell.
 */
export function GmailCard({ state, mail, auth, sending, sendMode, shadowMode = false, actions, children }: {
  state: GmailState; mail?: MailCheck; auth?: MailboxAuth | null; sending?: MailboxSendingHealth[]; sendMode?: IntegrationsState['sendMode'];
  shadowMode?: boolean; actions?: ReactNode; children?: ReactNode;
}) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const outcome = new URLSearchParams(window.location.search).get('gmail');
  const accounts = state.accounts;
  async function connect() {
    setBusy(true); setError('');
    try { const { url } = await api<{ url: string }>('/app-api/gmail/connect', {}); window.location.assign(url); }
    catch (e) { setError(e instanceof Error ? e.message : 'Gmail connection could not start.'); setBusy(false); }
  }
  const freshness = accounts.length ? mailFreshness(mail) : null;
  const health = outcome === 'failed' && !accounts.length
    ? { ...gmailHealth(state), tone: 'attention' as const, label: 'Needs attention',
      detail: 'Gmail was not connected. Choose Connect Gmail again and approve both permissions.' }
    : withFreshness(gmailHealth(state), freshness);
  const keyFacts: Fact[] = accounts.map((account, index) =>
    ({ label: index ? 'Also connected' : 'Mailbox', value: <strong>{account.email}</strong> }));
  const facts: Fact[] = accounts.map(account =>
    ({ label: accounts.length > 1 ? `${account.email} connected` : 'Connected', value: relativeTime(account.connectedAt) }));
  const summary = accounts.length
    ? 'Alder sends reminders from this mailbox and reads customer replies in it.'
    : state.configured ? 'Connect the mailbox Alder sends reminders from and reads replies in. About 2 minutes with Google sign-in.'
      : 'The mailbox Alder sends reminders from and reads replies in.';
  const message: CardMessage = error ? { kind: 'error', text: error }
    : outcome === 'connected' && accounts.length ? { kind: 'success', text: 'Gmail is connected.' } : null;
  const primary = accounts.length ? null
    : !state.configured ? <BlockedAction/> : <Button size="sm" disabled={busy} onClick={() => void connect()}>
      {busy ? 'Opening Google…' : 'Connect Gmail'}</Button>;
  const menu = accounts.length ? [{ label: 'Reconnect Gmail', icon: <RotateCcw size={15} aria-hidden/>,
    disabled: busy || !state.configured, onSelect: () => void connect() }] : [];
  return <IntegrationCard id="gmail" logo={gmailLogo} name="Gmail" role="Reminders and replies" health={health}
    summary={summary} freshness={freshness} keyFacts={keyFacts} facts={facts} message={message} primary={primary}
    actions={actions} menu={menu} reassurance={sendingLine(sendMode, shadowMode)}>{accounts.length && auth ? <MailboxHealth auth={auth}/> : null}
    {accounts.length && sending ? <SendingHealth mailboxes={sending}/> : null}{children}</IntegrationCard>;
}
